import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { HStack, Box, Text, Button, Input } from '@chakra-ui/react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { Formik } from 'formik';
import { fetchWrapper } from '../../helpers';
import API from '../../helpers/axiosInstance';

// Channel ID for E-Mail (see Channels list in Campaign/TemplateSelection.jsx)
const EMAIL_CHANNEL_ID = 3;

const EmailApi = () => {
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const [variables, setVariables] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  // Kept in memory only — never localStorage, so the key isn't readable by any
  // other script/tab or left behind after the page closes.
  const [apiKey, setApiKey] = useState('');

  const FormSchema = Yup.object().shape({
    template: Yup.string().required('Template is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    variables: Yup.array().of(
      Yup.object().shape({
        key: Yup.string(),
        value: Yup.string().required('Value is required'),
      })
    ),
  });

  const extractVariablesFromMessage = (message, messageCombine) => {
    try {
      if (messageCombine) {
        const parsed = typeof messageCombine === 'string' ? JSON.parse(messageCombine) : messageCombine;
        if (Array.isArray(parsed.variables) && parsed.variables.length > 0) {
          return parsed.variables.map((v) => ({ key: v.name, value: '' })).filter((v) => v.key);
        }
      }
    } catch (e) {
      console.error('Failed to parse message_combine', e);
    }
    const regex = /\$([a-zA-Z_]\w*)/g;
    const matches = message.match(regex) || [];
    return matches.map((match) => ({ key: match.substring(1), value: '' }));
  };

  const TemplateList = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/template/msg-temp-type-ids?channel_id=${EMAIL_CHANNEL_ID}`;
      const data = await fetchWrapper.get(url);
      const transformedData = data.data.map((item) => ({
        label: item?.name,
        value: item?.name,
        message: item?.message,
        ...item,
      }));
      setTemplateOptions(transformedData);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast('Error fetching templates', { type: 'error' });
    }
  };

  const APIKEYGET = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/customer/get-key?key_type=api_key`;
      const data = await fetchWrapper.get(url);
      setApiKey(data?.key || '');
    } catch (error) {
      console.error('Error fetching API key:', error);
    }
  };

  useEffect(() => {
    TemplateList();
    APIKEYGET();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTemplateChange = (option) => {
    if (!option) {
      setSelectedTemplate({});
      setVariables([]);
      return;
    }
    setSelectedTemplate(option);
    setVariables(extractVariablesFromMessage(option.message, option.message_combine));
  };

  const handleVariableChange = (index, value) => {
    setVariables((prev) => prev.map((variable, i) => (i === index ? { ...variable, value } : variable)));
  };

  const onSubmit = async () => {
    const variablesObject = {};
    variables.forEach((variable) => {
      if (variable.key && variable.value) {
        variablesObject[variable.key] = variable.value;
      }
    });

    const payload = {
      email,
      message_template_id: selectedTemplate?.id,
      ...variablesObject,
      preferred_channel: 'E-Mail',
    };

    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_SEND_API_URL}/v2/send-email`;
      await API.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          host: 'xnotify.ai',
        },
      });

      toast('Test email has been sent', { type: 'info' });
    } catch (error) {
      console.error('Error:', error);
      toast(error?.response?.data?.message || 'Error sending test email', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sms_body_section">
      <HStack justifyContent="space-between">
        <Box width="100%">
          <Formik
            enableReinitialize
            validationSchema={FormSchema}
            initialValues={{ template: selectedTemplate?.value || '', email, variables }}
            onSubmit={onSubmit}
          >
            {({ errors, touched, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                {/* Template Select */}
                <Box mb="10px">
                  <Text fontSize="16px" fontWeight="400" mb="5px">
                    Email Template <span style={{ color: 'red' }}>*</span>
                  </Text>
                  <Select
                    options={templateOptions}
                    value={templateOptions.find((option) => option.value === selectedTemplate?.value) || null}
                    onChange={handleTemplateChange}
                    placeholder="Select Template"
                    className={errors.template && touched.template ? 'is-invalid' : ''}
                  />
                  {errors.template && touched.template && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.template}
                    </Text>
                  )}
                </Box>

                {/* Email */}
                <Box mb="10px" mt="10px">
                  <Text fontSize="16px" fontWeight="400" mb="5px">
                    E-Mail <span style={{ color: 'red' }}>*</span>
                  </Text>
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Please Enter Email ID"
                    type="email"
                    bg="white"
                  />
                  {errors.email && touched.email && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.email}
                    </Text>
                  )}
                </Box>

                {/* Variables */}
                {variables.length > 0 && (
                  <>
                    <Text fontSize="16px" fontWeight={400}>
                      Variables <span style={{ color: 'red' }}>*</span>
                    </Text>
                    {variables.map((variable, index) => (
                      <HStack key={index} w="100%" mt="5px" alignItems="baseline">
                        <Box w="50%">
                          <Input value={variable.key} isReadOnly placeholder="Variable" bg="gray.100" />
                        </Box>
                        <Box w="50%">
                          <Input
                            value={variable.value}
                            onChange={(e) => handleVariableChange(index, e.target.value)}
                            placeholder="Value"
                          />
                        </Box>
                      </HStack>
                    ))}
                  </>
                )}

                {/* Submit */}
                <Button
                  isLoading={loading}
                  py="10px"
                  px="25px"
                  color="white"
                  background={'var(--brand-primary)'}
                  fontSize="14px"
                  fontWeight="500"
                  fontFamily="'Lato', sans-serif"
                  mb={4}
                  mt={4}
                  style={{ width: '100%' }}
                  type="submit"
                >
                  Send Test Email
                </Button>
              </form>
            )}
          </Formik>
        </Box>
      </HStack>
    </div>
  );
};

export default EmailApi;
